import { Link } from 'react-router-dom';

import type { ConnectionEntry } from '../api/types.js';

/**
 * First-run orientation (ARCHITECTURE.md §64, §65).
 *
 * Shown to a workspace with nothing connected, and to nobody else.
 *
 * It is derived entirely from real state — which platforms are connected,
 * whether the address is confirmed — rather than from a stored "onboarding
 * step" the operator could get stuck on. A checklist that remembers its own
 * progress is a checklist that eventually disagrees with reality, and this
 * codebase has already been bitten once by hand-maintained status text.
 *
 * It disappears on its own the moment the first platform connects. There is no
 * dismiss button, because there is nothing to dismiss: the reason it is on
 * screen is the reason it is still worth reading.
 */

export type GettingStartedProps = {
  connections: ConnectionEntry[];
  emailVerified: boolean;
};

export function GettingStarted({
  connections,
  emailVerified,
}: GettingStartedProps): React.JSX.Element | null {
  const connected = connections.filter((entry) => entry.connection !== null);

  // Its whole purpose is the empty state. Once chat is flowing, the operator
  // has better things on this page.
  if (connected.length > 0) return null;

  const connectable = connections.filter((entry) => entry.connectable);
  const canConnectSomething = connectable.length > 0;

  const steps = [
    {
      done: true,
      title: 'Create your workspace',
      body: 'Done — connections, chat history and members all live inside it.',
    },
    {
      done: emailVerified,
      title: 'Confirm your email address',
      body: emailVerified
        ? 'Confirmed.'
        : 'Check your inbox for the link. Password resets need a confirmed address.',
    },
    {
      done: false,
      title: 'Connect a platform',
      body: canConnectSomething
        ? 'Pick one on the Connections page. Each card has step-by-step setup instructions, including the exact values to copy.'
        : 'No platform is configured on this deployment yet. Whoever runs it needs to add the provider credentials first.',
      action: canConnectSomething ? { to: '/connections', label: 'Connect a platform' } : undefined,
    },
    {
      done: false,
      title: 'Watch your chat',
      body: 'Every connected platform appears in one feed, colour-coded and filterable.',
      action: { to: '/chat', label: 'Open Chat' },
    },
  ];

  return (
    <section className="panel getting-started" aria-labelledby="getting-started-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="getting-started-heading">
          Getting started
        </h2>
      </div>

      <div className="panel__body">
        <ol className="steps">
          {steps.map((step, index) => (
            <li key={step.title} className={`step${step.done ? ' step--done' : ''}`}>
              {/*
               * The marker states its state in text for assistive technology.
               * A tick that only exists as a shape or a colour says nothing to
               * a screen reader, and §65 forbids leaning on colour alone.
               */}
              <span className="step__marker" aria-hidden="true">
                {step.done ? (
                  <svg viewBox="0 0 24 24" width="14" height="14" focusable="false">
                    <path
                      d="M5 13l4 4L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  String(index + 1)
                )}
              </span>
              <span className="visually-hidden">{step.done ? 'Done: ' : 'To do: '}</span>

              <div className="step__content">
                <h3 className="step__title">{step.title}</h3>
                <p className="step__body">{step.body}</p>
                {step.action === undefined ? null : (
                  <Link className="btn btn--sm" to={step.action.to}>
                    {step.action.label}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
